import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

interface SelectableItem {
	id?: number;
	name?: string;
	Monitor_ID?: number;
	Monitor_Name?: string;
	[key: string]: any;
}

interface SearchableMultiSelectProps<T extends SelectableItem> {
	label?: string;
	selectedItems: T[];
	onChange: (items: T[]) => void;
	fetchOptions: () => Promise<T[]>;
	preloadedOptions?: T[];
	placeholder?: string;
	error?: string;
	disabled?: boolean;
}

const getId = <T extends SelectableItem>(item: T): number => {
	return item.Monitor_ID ?? item.id ?? 0;
};

const getName = <T extends SelectableItem>(item: T): string => {
	return item.Monitor_Name ?? item.name ?? "";
};

const SearchableMultiSelect = <T extends SelectableItem>({
	label,
	selectedItems,
	onChange,
	fetchOptions,
	preloadedOptions,
	placeholder,
	error,
	disabled = false,
}: SearchableMultiSelectProps<T>) => {
	const { t } = useTranslation();
	const [options, setOptions] = useState<T[]>([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [isOpen, setIsOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const resolvedPlaceholder = placeholder ?? t("common.searchAndSelect");

	useEffect(() => {
		if (preloadedOptions !== undefined) {
			setOptions(preloadedOptions);
			setLoading(false);
			return;
		}

		const loadOptions = async () => {
			try {
				setLoading(true);
				const data = await fetchOptions();
				setOptions(data);
			} catch (err) {
				console.error("Failed to fetch options:", err);
			} finally {
				setLoading(false);
			}
		};

		loadOptions();
	}, [fetchOptions, preloadedOptions]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				wrapperRef.current &&
				!wrapperRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	const filteredOptions = options.filter((option) =>
		getName(option).toLowerCase().includes(searchTerm.toLowerCase()),
	);

	const availableOptions = filteredOptions.filter(
		(option) => !selectedItems.some((selected) => getId(selected) === getId(option)),
	);

	const handleToggleItem = (item: T) => {
		const itemId = getId(item);
		if (selectedItems.some((selected) => getId(selected) === itemId)) {
			onChange(selectedItems.filter((selected) => getId(selected) !== itemId));
		} else {
			onChange([...selectedItems, item]);
		}
	};

	const handleRemoveItem = (itemId: number) => {
		onChange(selectedItems.filter((item) => getId(item) !== itemId));
	};

	return (
		<div className="w-full" ref={wrapperRef}>
			{label && (
				<label className="block text-gray-700 text-sm font-semibold mb-2">
					{label}
				</label>
			)}

			{selectedItems.length > 0 && (
				<div className="flex flex-wrap gap-2 mb-3">
					{selectedItems.map((item) => (
						<span
							key={getId(item)}
							className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gradient-to-r from-primary-100 to-primary-200 text-primary-800 border border-primary-300 shadow-sm"
						>
							{getName(item)}
							<button
								type="button"
								onClick={() => handleRemoveItem(getId(item))}
								disabled={disabled}
								className="ml-2 text-primary-500 hover:text-primary-700 disabled:cursor-not-allowed font-bold transition-colors"
							>
								×
							</button>
						</span>
					))}
				</div>
			)}

			<div className="relative">
				<input
					type="text"
					value={searchTerm}
					onChange={(e) => {
						setSearchTerm(e.target.value);
						setIsOpen(true);
					}}
					onFocus={() => setIsOpen(true)}
					placeholder={resolvedPlaceholder}
					disabled={disabled}
					className={`appearance-none border rounded-lg w-full py-2.5 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed transition-all duration-200 bg-white shadow-sm hover:shadow-md ${
						error ? "border-red-400 focus:ring-red-500" : "border-gray-300"
					}`}
				/>

				{isOpen && !disabled && (
					<div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-soft-lg max-h-60 overflow-auto">
						{loading ? (
							<div className="px-4 py-3 text-gray-500 text-sm">
								{t("common.loading")}
							</div>
						) : availableOptions.length === 0 ? (
							<div className="px-4 py-3 text-gray-500 text-sm">
								{searchTerm ? t("common.noResults") : t("common.noOptions")}
							</div>
						) : (
							<ul className="py-1">
								{availableOptions.map((option) => (
									<li
										key={getId(option)}
										onClick={() => handleToggleItem(option)}
										className="px-4 py-2.5 hover:bg-gradient-to-r hover:from-primary-50 hover:to-primary-100 cursor-pointer transition-colors duration-150"
									>
										{getName(option)}
									</li>
								))}
							</ul>
						)}
					</div>
				)}
			</div>

			{error && <p className="mt-1 text-sm text-red-600">{error}</p>}
		</div>
	);
};

export default SearchableMultiSelect;
